import { CSSProperties, useMemo, useRef, useState } from "react";
import {
  useFloating,
  autoUpdate,
  flip,
  shift,
  arrow,
  offset,
  Placement,
  Strategy,
} from "@floating-ui/react-dom";

import { TooltipPlacement } from "./tooltip.types";

type useTooltipArgs = {
  defaultPlacement: TooltipPlacement;
  strategy?: Strategy;
};

export const useTooltip = ({ defaultPlacement, strategy }: useTooltipArgs) => {
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  const arrowRef = useRef<HTMLDivElement>(null);

  const { refs, floatingStyles, placement, middlewareData } = useFloating({
    placement: defaultPlacement as Placement,
    strategy,
    middleware: [
      offset(8),
      flip(),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const showTooltip = () => setShouldShowTooltip(true);
  const hideTooltip = () => setShouldShowTooltip(false);

  const arrowStyles: CSSProperties = useMemo(() => {
    const { x, y } = middlewareData.arrow ?? {};
    const staticSide = {
      top: "bottom",
      right: "left",
      bottom: "top",
      left: "right",
    }[placement.split("-")[0] as TooltipPlacement];

    return {
      left: x != null ? `${x}px` : "",
      top: y != null ? `${y}px` : "",
      [staticSide as string]: "-4px",
    };
  }, [middlewareData.arrow, placement]);

  const resolvedPlacement = placement.split("-")[0] as TooltipPlacement;

  return {
    arrowStyles,
    tooltipStyles: floatingStyles,
    placement: resolvedPlacement,
    setReferenceElement: refs.setReference,
    setTooltipElement: refs.setFloating,
    arrowRef,
    shouldShowTooltip,
    showTooltip,
    hideTooltip,
  };
};
